/**
 * /payroll-l3/remittance — Remittance batch list and lifecycle.
 *
 * Lifecycle endpoints (all live):
 *   POST /remittance-batches/{id}/finalize      draft → finalized (generates report file)
 *   GET  /remittance-batches/{id}/download      stream report file
 *   POST /remittance-batches/{id}/mark-submitted finalized → submitted
 *   POST /remittance-batches/{id}/mark-paid     submitted|finalized → paid
 *   POST /remittance-batches/from-retro         create/merge draft from retro delta
 *   GET  /remittance-batches/{id}/lines         line-level detail (shown in detail dialog)
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/remittance")({
  component: RemittancePage,
});

interface Batch {
  id: string;
  fringe_fund_id: string | null;
  local_id: string | null;
  period_start: string;
  period_end: string;
  due_date: string | null;
  status: "draft" | "finalized" | "submitted" | "paid" | string;
  total_amount: string | null;
  report_format: string | null;
  report_file_url: string | null;
  finalized_at: string | null;
  submitted_at: string | null;
}

interface BatchLine {
  id: string;
  employee_id: string;
  hours_worked: string;
  hours_ot: string;
  rate: string;
  amount: string;
}

interface FringeFund {
  id: string;
  name: string;
}

function fmtMoney(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function statusChip(s: string) {
  const map: Record<string, "default" | "warning" | "info" | "success" | "primary"> = {
    draft: "default",
    finalized: "info",
    submitted: "primary",
    paid: "success",
  };
  return <Chip label={s} size="small" color={map[s] ?? "default"} />;
}

// ─── From-retro dialog ───────────────────────────────────────────────────────

interface RetroBatchCreate {
  fringe_fund_id: string;
  period_start: string;
  period_end: string;
  delta_amount?: string;
  line_items: object[];
}

function FromRetroDialog({
  open,
  onClose,
  funds,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  funds: FringeFund[];
  onCreated: () => void;
}) {
  const [form, setForm] = useState<RetroBatchCreate>({
    fringe_fund_id: "",
    period_start: "",
    period_end: "",
    delta_amount: "",
    line_items: [],
  });
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api<Batch>("/api/payroll-l3/remittance-batches/from-retro", {
        method: "POST",
        body: {
          fringe_fund_id: form.fringe_fund_id,
          period_start: form.period_start,
          period_end: form.period_end,
          delta_amount: form.delta_amount || undefined,
          line_items: [],
        },
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      setForm({ fringe_fund_id: "", period_start: "", period_end: "", delta_amount: "", line_items: [] });
      setErr(null);
    },
    onError: (e: unknown) =>
      setErr(e instanceof Error ? e.message : "Failed to create retro batch"),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>Create Retro Adjustment Batch</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            select
            label="Fringe Fund *"
            size="small"
            fullWidth
            value={form.fringe_fund_id}
            onChange={(e) => setForm((p) => ({ ...p, fringe_fund_id: e.target.value }))}
          >
            {funds.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              type="date"
              label="Period Start *"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.period_start}
              onChange={(e) => setForm((p) => ({ ...p, period_start: e.target.value }))}
            />
            <TextField
              type="date"
              label="Period End *"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.period_end}
              onChange={(e) => setForm((p) => ({ ...p, period_end: e.target.value }))}
            />
          </Stack>
          <TextField
            label="Delta Amount (optional)"
            size="small"
            fullWidth
            type="number"
            inputProps={{ step: "0.01" }}
            value={form.delta_amount}
            onChange={(e) => setForm((p) => ({ ...p, delta_amount: e.target.value }))}
            helperText="Leave blank if amount comes from line items"
          />
          <Alert severity="info" sx={{ fontSize: 12 }}>
            This creates a draft retro-adjustment batch for the selected fund and period.
            Line items can be added by the system from the AI retro-detect output.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.fringe_fund_id || !form.period_start || !form.period_end || mut.isPending}
          onClick={() => mut.mutate()}
        >
          Create Batch
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Discrepancy scan result dialog ─────────────────────────────────────────

interface DiscrepancyResult {
  batch_id: string;
  skipped: boolean;
  reason?: string;
  current_total?: string;
  rolling_average?: string;
  swing_pct?: string;
  threshold_pct?: string;
  prior_count?: number;
  narrative?: string;
  likely_cause_category?: string;
  ai?: string;
}

function DiscrepancyResultDialog({
  result,
  onClose,
}: {
  result: DiscrepancyResult | null;
  onClose: () => void;
}) {
  if (!result) return null;
  return (
    <Dialog open={!!result} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeIcon fontSize="small" color="primary" />
            <span>Discrepancy Scan Result</span>
          </Stack>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {result.skipped ? (
          <Alert severity="info">{result.reason ?? "No significant swing detected."}</Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600}>
                {result.swing_pct && `${Number(result.swing_pct) > 0 ? "+" : ""}${Number(result.swing_pct).toFixed(1)}% vs rolling average`}
              </Typography>
              <Typography variant="body2">{result.narrative}</Typography>
            </Alert>
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Current</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {result.current_total ? `$${Number(result.current_total).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Rolling avg ({result.prior_count} periods)</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {result.rolling_average ? `$${Number(result.rolling_average).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}
                </Typography>
              </Box>
              {result.likely_cause_category && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Likely cause</Typography>
                  <Typography variant="body2">{result.likely_cause_category.replace(/_/g, " ")}</Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

function RemittancePage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: "", fund: "", periodStart: "", periodEnd: "" });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [retroOpen, setRetroOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [discrepancyResult, setDiscrepancyResult] = useState<DiscrepancyResult | null>(null);

  const fundsQ = useQuery({
    queryKey: ["payroll-l3", "fringe-funds"],
    queryFn: () => api<FringeFund[]>("/api/payroll-l3/fringe-funds"),
  });

  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.fund) params.set("fringe_fund_id", filters.fund);
  if (filters.periodStart) params.set("period_start", filters.periodStart);
  if (filters.periodEnd) params.set("period_end", filters.periodEnd);
  const qs = params.toString();

  const batchesQ = useQuery({
    queryKey: ["payroll-l3", "remittance-batches", filters],
    queryFn: () =>
      api<Batch[]>(`/api/payroll-l3/remittance-batches${qs ? `?${qs}` : ""}`),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["payroll-l3", "remittance-batches"] });

  const finalizeMut = useMutation({
    mutationFn: (id: string) =>
      api<Batch>(`/api/payroll-l3/remittance-batches/${id}/finalize`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : "Finalize failed"),
  });

  const submitMut = useMutation({
    mutationFn: (id: string) =>
      api<Batch>(`/api/payroll-l3/remittance-batches/${id}/mark-submitted`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : "Mark-submitted failed"),
  });

  const paidMut = useMutation({
    mutationFn: (id: string) =>
      api<Batch>(`/api/payroll-l3/remittance-batches/${id}/mark-paid`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : "Mark-paid failed"),
  });

  const discrepancyMut = useMutation({
    mutationFn: (id: string) =>
      api<DiscrepancyResult>(
        `/api/payroll-l3/ai/remittance-batches/${id}/discrepancy-scan`,
        { method: "POST" }
      ),
    onSuccess: (result) => setDiscrepancyResult(result),
    onError: (e: unknown) => setActionError(e instanceof Error ? e.message : "Discrepancy scan failed"),
  });

  const isBusy = finalizeMut.isPending || submitMut.isPending || paidMut.isPending;

  const fundName = (id: string | null) =>
    id ? fundsQ.data?.find((f) => f.id === id)?.name ?? id.slice(0, 8) : "—";

  const downloadReport = (b: Batch) => {
    window.open(`/api/payroll-l3/remittance-batches/${b.id}/download`, "_blank");
  };

  const renderActions = (b: Batch) => {
    if (b.status === "draft") {
      return (
        <Button
          size="small"
          variant="contained"
          disabled={isBusy}
          onClick={() => { setActionError(null); finalizeMut.mutate(b.id); }}
        >
          Finalize
        </Button>
      );
    }
    if (b.status === "finalized") {
      return (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadReport(b)}
          >
            Report
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={isBusy}
            onClick={() => { setActionError(null); submitMut.mutate(b.id); }}
          >
            Mark Submitted
          </Button>
        </Stack>
      );
    }
    if (b.status === "submitted") {
      return (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadReport(b)}
          >
            Report
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={isBusy}
            onClick={() => { setActionError(null); paidMut.mutate(b.id); }}
          >
            Mark Paid
          </Button>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>Remittance Batches</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpHint docPath="payroll-l3/remittance.md" label="Remittance help" />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRetroOpen(true)}
          >
            Retro Adjustment
          </Button>
        </Stack>
      </Stack>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <TextField
          select size="small" label="Status" value={filters.status}
          onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          {["draft", "finalized", "submitted", "paid"].map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Fund" value={filters.fund}
          onChange={(e) => setFilters((p) => ({ ...p, fund: e.target.value }))}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {(fundsQ.data ?? []).map((f) => (
            <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" type="date" label="Period Start ≥"
          value={filters.periodStart}
          onChange={(e) => setFilters((p) => ({ ...p, periodStart: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" type="date" label="Period End ≤"
          value={filters.periodEnd}
          onChange={(e) => setFilters((p) => ({ ...p, periodEnd: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      {batchesQ.isLoading && <CircularProgress size={24} />}
      {batchesQ.error && <Alert severity="error">Failed to load remittance batches.</Alert>}

      {batchesQ.data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fund</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {batchesQ.data.map((b) => (
              <TableRow key={b.id} hover>
                <TableCell>{fundName(b.fringe_fund_id)}</TableCell>
                <TableCell>
                  {fmtDate(b.period_start)} → {fmtDate(b.period_end)}
                </TableCell>
                <TableCell>{fmtDate(b.due_date)}</TableCell>
                <TableCell>{statusChip(b.status)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {fmtMoney(b.total_amount)}
                </TableCell>
                <TableCell>{renderActions(b)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Button size="small" onClick={() => setDetailId(b.id)}>Lines</Button>
                    {["finalized", "submitted", "paid"].includes(b.status) && (
                      <Button
                        size="small"
                        startIcon={<AutoAwesomeIcon fontSize="inherit" />}
                        disabled={discrepancyMut.isPending}
                        onClick={() => discrepancyMut.mutate(b.id)}
                        title="AI discrepancy scan"
                      >
                        Scan
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {batchesQ.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  No remittance batches — they're auto-created by the pay-run union hook
                  when a pay period closes, or manually via Retro Adjustment above.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <BatchLinesDialog batchId={detailId} onClose={() => setDetailId(null)} fundName={fundName} />

      <DiscrepancyResultDialog
        result={discrepancyResult}
        onClose={() => setDiscrepancyResult(null)}
      />

      <FromRetroDialog
        open={retroOpen}
        onClose={() => setRetroOpen(false)}
        funds={fundsQ.data ?? []}
        onCreated={invalidate}
      />
    </Box>
  );
}

// ─── Lines detail dialog ─────────────────────────────────────────────────────

function BatchLinesDialog({
  batchId,
  onClose,
  fundName,
}: {
  batchId: string | null;
  onClose: () => void;
  fundName: (id: string | null) => string;
}) {
  const open = !!batchId;

  const batchQ = useQuery({
    queryKey: ["payroll-l3", "remittance-batches", batchId],
    queryFn: () => api<Batch>(`/api/payroll-l3/remittance-batches/${batchId}`),
    enabled: open,
  });
  const linesQ = useQuery({
    queryKey: ["payroll-l3", "remittance-batches", batchId, "lines"],
    queryFn: () =>
      api<BatchLine[]>(`/api/payroll-l3/remittance-batches/${batchId}/lines`),
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>Remittance Lines</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {batchQ.data && (
          <Box mb={2}>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="text.secondary">Fund</Typography>
                <Typography fontWeight={600}>{fundName(batchQ.data.fringe_fund_id)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Period</Typography>
                <Typography>
                  {new Date(batchQ.data.period_start).toLocaleDateString()} →{" "}
                  {new Date(batchQ.data.period_end).toLocaleDateString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box mt={0.5}>{batchQ.data && <Chip label={batchQ.data.status} size="small" />}</Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography fontWeight={600}>
                  {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                    Number(batchQ.data.total_amount ?? 0)
                  )}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {linesQ.isLoading && <CircularProgress size={20} />}
        {linesQ.data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="right">OT hrs</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linesQ.data.map((l) => (
                <TableRow key={l.id}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {l.employee_id.slice(0, 8)}…
                  </TableCell>
                  <TableCell align="right">{Number(l.hours_worked).toFixed(2)}</TableCell>
                  <TableCell align="right">{Number(l.hours_ot).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(l.rate))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(l.amount))}
                  </TableCell>
                </TableRow>
              ))}
              {linesQ.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 3 }}>
                    No line items on this batch.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
